import { Component, EventEmitter, NgZone, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { ISignatureFieldEvent, SignatureField } from '../../../../interfaces/signature';
import { ActiveContentControlMonitor } from '../../../../services/document-monitors/active-content-control-monitor';
import { ContentControlId } from '../../../../services/document-monitors/content-control-id';
import { ContentControlToSignatureFieldService } from '../../../../services/esignature/content-control-to-signature-field.service';
import { DocusignOptionsService, PropertiesKeys } from './docusign-options.service';

@Component({
    selector: 'app-docusign-options',
    templateUrl: './docusign-options.component.html',
    styleUrls: ['./docusign-options.component.scss', '../../../field-selection/field-config/field-config.component.scss'],
    standalone: false
})
export class DocusignOptionsComponent implements OnDestroy {
  @Output()
  fieldClickedEvent = new EventEmitter<ISignatureFieldEvent>();

  @Output()
  closeOptionsEvent = new EventEmitter<void>();

  activeField: ({ contentControlId: number } & SignatureField) | undefined; // this is NOT a deep clone! so don't change it!

  showRequired = false;
  isRequired = true;

  showDateValidation = false;
  dateValidation = DocusignOptionsService.DATE_VALIDATION_DEFAULT;

  showUrlEntry = false;
  urlValue = DocusignOptionsService.DEFAULT_URL;
  urlDisplayText = DocusignOptionsService.DEFAULT_URL_DISPLAY_TEXT;

  showToolTip = false;
  toolTip = DocusignOptionsService.DEFAULT_TOOL_TIP;

  showTabValue = false;
  tabValue = DocusignOptionsService.DEFAULT_TAB_VALUE;

  modified = false;

  private activeContentControlSubscription: Subscription;

  protected readonly DocusignOptionsService = DocusignOptionsService;

  constructor(
    private activeContentControlMonitor: ActiveContentControlMonitor,
    private contentControlToSignatureFieldService: ContentControlToSignatureFieldService,
    private ngZone: NgZone
  ) {
    this.activeContentControlSubscription = activeContentControlMonitor.activeContentControlChanged$.subscribe({
      next: (acc) => this.activeControlChanged(acc),
    });
  }

  ngOnDestroy() {
    console.debug('Options Component destroyed.');
    this.activeContentControlSubscription?.unsubscribe();
  }

  closeOptions() {
    this.closeOptionsEvent.emit();
  }

  onUpdateClick(): void {
    if (!this.activeField) {
      return;
    }

    const { contentControlId, ...field } = this.activeField;

    this.addAdditionalProperties(field);

    this.fieldClickedEvent.emit({
      providerName: 'docusign',
      field: field,
      contentControlId: this.activeField.contentControlId,
    });

    this.modified = false;
  }

  private addAdditionalProperties(field: any) {
    DocusignOptionsService.updateRequired(field, this.isRequired);
    DocusignOptionsService.updateDateValidation(field, this.dateValidation);
    DocusignOptionsService.updateToolTip(field, this.toolTip);
    DocusignOptionsService.updateUrlEntry(field, this.urlValue, this.urlDisplayText);
    DocusignOptionsService.updateTabValue(field, this.tabValue);
  }

  private activeControlChanged(activeContentControl: ContentControlId | undefined): void {
    console.debug('%cdocusign-options-component - active content control changed handler', 'color:#00aaaa');
    this.ngZone.run(() => {
      this.modified = false;

      if (!activeContentControl) {
        this.resetView();
        return;
      }

      if (activeContentControl.tag.startsWith('docusign-')) {
        const field = this.contentControlToSignatureFieldService.get(activeContentControl.id);
        this.activeField = field ? { contentControlId: activeContentControl.id, ...field.field } : undefined;
        this.setupOptions();
        return;
      }

      this.resetView();
    });
  }

  private resetView() {
    this.isRequired = DocusignOptionsService.REQUIRED_DEFAULT;
    this.toolTip = DocusignOptionsService.DEFAULT_TOOL_TIP;
    this.urlValue = DocusignOptionsService.DEFAULT_URL;
    this.urlDisplayText = DocusignOptionsService.DEFAULT_URL_DISPLAY_TEXT;
    this.dateValidation = DocusignOptionsService.DATE_VALIDATION_DEFAULT;
    this.tabValue = DocusignOptionsService.DEFAULT_TAB_VALUE;
  }

  private setupOptions() {
    this.showRequired = DocusignOptionsService.supportsRequired(this.activeField!.type);
    this.isRequired =
      this.activeField &&
      (DocusignOptionsService.legacyFieldWithNoRequiredOption(this.activeField) || this.activeField.properties[PropertiesKeys.REQUIRED]);

    this.showDateValidation = DocusignOptionsService.supportsDateValidation(this.activeField!.type);
    this.dateValidation = this.activeField && this.activeField.properties[PropertiesKeys.DATE_VALIDATION_TYPE];

    this.showToolTip = DocusignOptionsService.supportsToolTip(this.activeField!.type);
    this.toolTip = this.activeField && this.activeField.properties[PropertiesKeys.TOOL_TIP];

    this.showUrlEntry = DocusignOptionsService.supportsUrlEntry(this.activeField!.type);
    this.urlValue = this.activeField && this.activeField.properties[PropertiesKeys.URL];
    this.urlDisplayText = this.activeField && this.activeField.properties[PropertiesKeys.URL_DISPLAY_TEXT];

    this.showTabValue = DocusignOptionsService.supportsTabValue(this.activeField!.type);
    this.tabValue = this.activeField && this.activeField.properties[PropertiesKeys.TAB_VALUE];
  }
}
